import React from 'react'
import {KTIcon} from '../../../../_metronic/helpers'
import {Movimiento} from '../core/models'
import {fetchWithAuth} from '../../../core/utils/fetchWithAuth'
import type {PagoParaEditar} from './EditarPagoModal'

type Props = {
  movimientos: Movimiento[]
  loading: boolean
  onEditPago?: (pago: PagoParaEditar) => void
  onDeletePago?: (idPago: string) => void
}

const MovimientosTable: React.FC<Props> = ({movimientos, loading, onEditPago, onDeletePago}) => {
  const esPago = (mov: Movimiento) => mov.tipo === 'HABER' && mov.idPago
  const handleDownloadComprobante = async (comprobantePath: string, e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    
    try {
      const baseUrl = process.env.REACT_APP_API_URL?.replace('/services', '') || ''
      const fullUrl = `${baseUrl}${comprobantePath}`
      
      // Si la URL incluye parámetros de autenticación en la query, usar enlace directo
      // Si no, intentar descarga autenticada
      if (comprobantePath.includes('sig=')) {
        // La URL ya incluye firma, abrir directamente
        window.open(fullUrl, '_blank')
      } else {
        // Intentar descarga autenticada
        const token = localStorage.getItem('X-TOKEN')
        if (token) {
          const response = await fetchWithAuth(fullUrl, {
            method: 'GET',
            autoAttachToken: true,
          })
          
          if (response.ok) {
            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `comprobante_${Date.now()}.pdf`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
          } else {
            // Si falla la descarga autenticada, intentar enlace directo
            window.open(fullUrl, '_blank')
          }
        } else {
          window.open(fullUrl, '_blank')
        }
      }
    } catch (err) {
      console.error('Error al descargar comprobante:', err)
      // Fallback: abrir enlace directo
      const baseUrl = process.env.REACT_APP_API_URL?.replace('/services', '') || ''
      window.open(`${baseUrl}${comprobantePath}`, '_blank')
    }
  }
  if (loading) {
    return (
      <div className='card'>
        <div className='card-body'>
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bold text-muted bg-light'>
                  {Array.from({length: 7}).map((_, i) => (
                    <th key={i}>
                      <div className='placeholder-glow'>
                        <span className='placeholder col-8'></span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({length: 5}).map((_, i) => (
                  <tr key={i}>
                    {Array.from({length: 7}).map((_, j) => (
                      <td key={j}>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-6'></span>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  if (movimientos.length === 0) {
    return (
      <div className='card'>
        <div className='card-body text-center py-20'>
          <KTIcon iconName='magnifier' className='fs-3x text-gray-400 mb-5' />
          <h3 className='text-gray-800 fw-bold mb-3'>No se encontraron movimientos</h3>
          <p className='text-muted'>Intenta ajustar los filtros de búsqueda.</p>
        </div>
      </div>
    )
  }

  return (
    <div className='card'>
      <div className='card-body py-3'>
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold text-muted bg-light'>
                <th className='ps-4 min-w-100px rounded-start'>Fecha</th>
                <th className='min-w-150px'>Socio</th>
                <th className='min-w-100px'>Tipo</th>
                <th className='min-w-100px'>Referencia</th>
                <th className='min-w-125px'>Medio Pago</th>
                <th className='min-w-125px text-end'>Monto</th>
                <th className='min-w-100px text-center'>Comprobante</th>
                {(onEditPago || onDeletePago) && (
                  <th className='min-w-90px text-end pe-4 rounded-end'>Acciones</th>
                )}
              </tr>
            </thead>
            <tbody>
              {movimientos.map((mov) => (
                <tr key={mov.id}>
                  <td className='ps-4'>
                    <div className='d-flex flex-column'>
                      <span className='text-gray-800 fw-bold fs-6'>
                        {new Date(mov.fecha).toLocaleDateString('es-UY')}
                      </span>
                      <span className='text-muted fw-semibold fs-7'>ID: {mov.id}</span>
                    </div>
                  </td>
                  <td>
                    <div className='d-flex flex-column'>
                      <span className='text-gray-800 fw-bold fs-6'>{mov.nombreFantasia}</span>
                      <span className='text-muted fw-semibold fs-7'>
                        {mov.razonSocial} (N° {mov.nroSocio})
                      </span>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`badge badge-light-${
                        mov.tipo === 'HABER' ? 'success' : 'danger'
                      }`}
                    >
                      {mov.tipo}
                    </span>
                  </td>
                  <td>
                    <div className='d-flex flex-column'>
                      {mov.nroFactura && (
                        <span className='text-gray-800 fs-7'>Factura: #{mov.nroFactura}</span>
                      )}
                      {mov.idPago && (
                        <span className='text-gray-600 fs-7'>Pago: #{mov.idPago}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className='text-gray-800 fw-bold d-block fs-6'>
                      {mov.medioPago || '-'}
                    </span>
                  </td>
                  <td className='text-end'>
                    <span
                      className={`text-${
                        mov.tipo === 'HABER' ? 'success' : 'danger'
                      } fw-bold fs-6`}
                    >
                      {mov.tipo === 'HABER' ? '+' : '-'} ${' '}
                      {parseFloat(mov.monto).toLocaleString('es-UY', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </td>
                  <td className='text-center'>
                    {mov.comprobante ? (
                      <a
                        href={`${process.env.REACT_APP_API_URL?.replace('/services', '')}${mov.comprobante}`}
                        onClick={(e) => handleDownloadComprobante(mov.comprobante!, e)}
                        className='btn btn-sm btn-icon btn-light-primary'
                        title='Descargar comprobante'
                      >
                        <KTIcon iconName='file-down' className='fs-3' />
                      </a>
                    ) : (
                      <span className='text-muted fs-7'>-</span>
                    )}
                  </td>
                  {(onEditPago || onDeletePago) && (
                    <td className='text-end pe-4'>
                      {esPago(mov) ? (
                        <div className='d-flex justify-content-end flex-shrink-0 gap-1'>
                          {onEditPago && (
                            <button
                              type='button'
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                              title='Editar pago'
                              onClick={() =>
                                onEditPago({
                                  idPago: mov.idPago,
                                  monto: mov.monto,
                                  fecha: mov.fecha,
                                  observaciones: mov.observaciones ?? null,
                                })
                              }
                            >
                              <KTIcon iconName='pencil' className='fs-3' />
                            </button>
                          )}
                          {onDeletePago && (
                            <button
                              type='button'
                              className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
                              title='Eliminar pago'
                              onClick={() => onDeletePago(mov.idPago)}
                            >
                              <KTIcon iconName='trash' className='fs-3' />
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className='text-muted fs-7'>-</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export {MovimientosTable}
